import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Professional PDF colors
const colors = {
  primary: [249, 115, 22],    // Orange
  dark: [24, 24, 27],          // Dark header/footer
  light: [250, 250, 250],      // Light background
  gray: [100, 100, 100],       // Text gray
  black: [0, 0, 0],            // Black text
  white: [255, 255, 255],      // White
  border: [229, 229, 229],     // Light border
  success: [34, 197, 94],      // Green
  info: [59, 130, 246],        // Blue
};

// Logo as base64 (simplified P logo)
const drawLogo = (doc, x, y, scale = 1) => {
  const size = 12 * scale;
  // Orange P background
  doc.setFillColor(...colors.primary);
  doc.roundedRect(x, y, size, size, 2, 2, 'F');
  // White P letter
  doc.setTextColor(...colors.white);
  doc.setFontSize(10 * scale);
  doc.setFont("helvetica", "bold");
  doc.text("P", x + size/2, y + size * 0.72, { align: 'center' });
};

// Professional header with company info
const drawHeader = (doc, title, number, date, user) => {
  const pageWidth = doc.internal.pageSize.width;
  
  // Light gray background for header
  doc.setFillColor(...colors.light);
  doc.rect(0, 0, pageWidth, 50, 'F');
  
  // Orange accent line at top
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 4, 'F');
  
  // Logo
  drawLogo(doc, 15, 12, 1.5);
  
  // Company name
  doc.setTextColor(...colors.black);
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(user?.company_name || "PServicePro", 38, 22);
  
  // Company details
  doc.setTextColor(...colors.gray);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  const companyInfo = [];
  if (user?.address) companyInfo.push(user.address);
  if (user?.city) companyInfo.push(`${user.cap || ''} ${user.city} (${user.province || ''})`);
  if (user?.vat_number) companyInfo.push(`P.IVA: ${user.vat_number}`);
  if (user?.phone) companyInfo.push(`Tel: ${user.phone}`);
  if (user?.pec) companyInfo.push(`PEC: ${user.pec}`);
  
  let infoY = 28;
  companyInfo.slice(0, 3).forEach(info => {
    doc.text(info, 38, infoY);
    infoY += 5;
  });
  
  // Document title and number - right side
  doc.setFillColor(...colors.primary);
  doc.roundedRect(pageWidth - 75, 10, 60, 30, 3, 3, 'F');
  
  doc.setTextColor(...colors.white);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(title, pageWidth - 45, 20, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text(`N° ${number}`, pageWidth - 45, 32, { align: 'center' });
  
  // Date below the box
  doc.setTextColor(...colors.gray);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Data: ${date}`, pageWidth - 45, 46, { align: 'center' });
};

// Customer and vehicle info section
const drawCustomerVehicleSection = (doc, customer, vehicle, startY) => {
  const pageWidth = doc.internal.pageSize.width;
  const colWidth = (pageWidth - 30) / 2;
  
  // Customer box - compact version
  doc.setFillColor(...colors.white);
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, startY, colWidth - 5, 42, 2, 2, 'FD');
  
  doc.setFillColor(...colors.primary);
  doc.roundedRect(15, startY, colWidth - 5, 7, 2, 2, 'F');
  doc.setTextColor(...colors.white);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("CLIENTE", 20, startY + 5);
  
  doc.setTextColor(...colors.black);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(customer.name || "", 20, startY + 15);
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...colors.gray);
  // Compact: CF and address on same line
  doc.text(`CF: ${customer.fiscal_code || ""} - ${customer.address || ""}`, 20, startY + 22, { maxWidth: colWidth - 15 });
  doc.text(`Tel: ${customer.phone || ""} - ${customer.email || ""}`, 20, startY + 29, { maxWidth: colWidth - 15 });
  // Additional fields if available
  if (customer.city || customer.cap || customer.province) {
    doc.text(`${customer.cap || ""} ${customer.city || ""} (${customer.province || ""})`, 20, startY + 36, { maxWidth: colWidth - 15 });
  }
  
  // Vehicle box - compact version
  doc.setFillColor(...colors.white);
  doc.roundedRect(15 + colWidth, startY, colWidth - 5, 42, 2, 2, 'FD');
  
  doc.setFillColor(...colors.primary);
  doc.roundedRect(15 + colWidth, startY, colWidth - 5, 7, 2, 2, 'F');
  doc.setTextColor(...colors.white);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("VEICOLO", 20 + colWidth, startY + 5);
  
  // Vehicle brand + model compact
  doc.setTextColor(...colors.black);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  const _brand = (vehicle.brand || "").trim();
  const _model = (vehicle.model || "").trim();
  let vehicleFullName = (_brand && _model && _model.toLowerCase().startsWith(_brand.toLowerCase()))
    ? _model
    : `${_brand} ${_model}`.trim();
  if (vehicle.version) vehicleFullName = `${vehicleFullName} ${vehicle.version}`.trim();
  const truncatedVehicleName = vehicleFullName.length > 38 ? vehicleFullName.substring(0, 35) + "..." : vehicleFullName;
  doc.text(truncatedVehicleName, 20 + colWidth, startY + 13);
  
  doc.setTextColor(...colors.primary);
  doc.setFontSize(12);
  doc.text(vehicle.plate || "", 20 + colWidth, startY + 23);
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...colors.gray);
  // Row 1: Anno, Km, Carburante
  const vehicleInfo = [];
  if (vehicle.year) vehicleInfo.push(`Anno: ${vehicle.year}`);
  if (vehicle.km) vehicleInfo.push(`Km: ${Number(vehicle.km).toLocaleString()}`);
  if (vehicle.fuel_type) vehicleInfo.push(`Alim.: ${vehicle.fuel_type}`);
  doc.text(vehicleInfo.join(" - "), 20 + colWidth, startY + 26);
  // Row 2: Codice motore
  if (vehicle.engine_code) {
    doc.text(`Cod. motore: ${vehicle.engine_code}`, 20 + colWidth, startY + 31);
  }
  // Row 3: VIN
  if (vehicle.vin) {
    const vinText = vehicle.vin.length > 20 ? `VIN: ${vehicle.vin.substring(0, 17)}...` : `VIN: ${vehicle.vin}`;
    doc.text(vinText, 20 + colWidth, startY + 36);
  }
  
  return startY + 48;
};

// Items table
const drawItemsTable = (doc, items, laborCost, laborHours, startY) => {
  // Check if there are labor rows in items
  const hasLaborRows = items.some(item => 
    item.is_labor || item.line_type === "MANODOPERA"
  );
  
  const tableData = items.map(item => {
    // Calculate subtotal if not present
    const qty = parseFloat(item.quantity) || 1;
    const price = parseFloat(item.unit_price) || 0;
    const calculatedSubtotal = item.subtotal || (qty * price);
    
    return [
      item.item_code || "-",
      item.description || "",
      item.brand || "-",
      item.quantity?.toString() || "1",
      `€ ${(item.unit_price || 0).toFixed(2)}`,
      `${item.vat_rate || 22}%`,
      `€ ${calculatedSubtotal.toFixed(2)}`
    ];
  });
  
  // Add labor row from field ONLY if no labor rows exist in items
  if (laborHours > 0 && !hasLaborRows) {
    tableData.push([
      "-",
      "Manodopera",
      "-",
      `${laborHours}h`,
      `€ ${laborCost.toFixed(2)}/h`,
      "22%",
      `€ ${(laborCost * laborHours).toFixed(2)}`
    ]);
  }
  
  autoTable(doc, {
    startY: startY,
    head: [['Cod.', 'Descrizione', 'Marca', 'Qtà', 'Prezzo', 'IVA', 'Subtotale']],
    body: tableData,
    theme: 'plain',
    headStyles: {
      fillColor: colors.light,
      textColor: colors.black,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 4,
      lineColor: colors.border,
      lineWidth: 0.5
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 4,
      lineColor: colors.border,
      lineWidth: 0.2
    },
    alternateRowStyles: {
      fillColor: [252, 252, 252]
    },
    columnStyles: {
      0: { cellWidth: 18 },
      1: { cellWidth: 55 },
      2: { cellWidth: 25 },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 25, halign: 'right' },
      5: { cellWidth: 15, halign: 'center' },
      6: { cellWidth: 25, halign: 'right' }
    },
    margin: { left: 15, right: 15 }
  });
  
  return doc.lastAutoTable.finalY;
};

// Totals box
const drawTotals = (doc, subtotal, vatTotal, total, startY) => {
  const pageWidth = doc.internal.pageSize.width;
  const boxWidth = 80;
  const boxX = pageWidth - boxWidth - 15;
  
  doc.setFillColor(...colors.light);
  doc.setDrawColor(...colors.border);
  doc.roundedRect(boxX, startY, boxWidth, 45, 2, 2, 'FD');
  
  doc.setFontSize(9);
  doc.setTextColor(...colors.gray);
  doc.text("Imponibile:", boxX + 5, startY + 12);
  doc.text("IVA:", boxX + 5, startY + 22);
  
  doc.setTextColor(...colors.black);
  doc.setFont("helvetica", "normal");
  doc.text(`€ ${subtotal.toFixed(2)}`, boxX + boxWidth - 5, startY + 12, { align: 'right' });
  doc.text(`€ ${vatTotal.toFixed(2)}`, boxX + boxWidth - 5, startY + 22, { align: 'right' });
  
  // Total line
  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(1);
  doc.line(boxX + 5, startY + 28, boxX + boxWidth - 5, startY + 28);
  
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("TOTALE:", boxX + 5, startY + 40);
  doc.setTextColor(...colors.primary);
  doc.text(`€ ${total.toFixed(2)}`, boxX + boxWidth - 5, startY + 40, { align: 'right' });
  
  return startY + 50;
};

// Footer
const drawFooter = (doc, includeGDPR = true) => {
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  
  // GDPR text above the footer
  if (includeGDPR) {
    doc.setTextColor(...colors.gray);
    doc.setFontSize(6);
    doc.setFont("helvetica", "normal");
    const gdprText = "Informativa Privacy (GDPR): I dati personali raccolti saranno trattati in conformità al Regolamento UE 2016/679 (GDPR) esclusivamente per l'esecuzione del servizio richiesto. Il titolare del trattamento garantisce la riservatezza e la sicurezza dei dati. Per informazioni: privacy@pservice.it";
    const gdprLines = doc.splitTextToSize(gdprText, pageWidth - 30);
    doc.text(gdprLines, 15, pageHeight - 28);
  }
  
  // Footer line
  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(2);
  doc.line(15, pageHeight - 18, pageWidth - 15, pageHeight - 18);
  
  // Footer text
  doc.setTextColor(...colors.gray);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text("PServicePro - Gestione Officina Professionale", pageWidth / 2, pageHeight - 12, { align: 'center' });
  doc.text(`Documento generato il ${new Date().toLocaleString('it-IT')}`, pageWidth / 2, pageHeight - 7, { align: 'center' });
};

// Signature area
const drawSignatureArea = (doc, startY, signatureData = null) => {
  const pageWidth = doc.internal.pageSize.width;
  
  doc.setFontSize(9);
  doc.setTextColor(...colors.gray);
  doc.text("Data: _______________________", 25, startY + 10);
  
  doc.text("Firma Cliente:", pageWidth / 2 + 10, startY + 5);
  
  if (signatureData) {
    // Add signature image
    doc.addImage(signatureData, 'PNG', pageWidth / 2 + 10, startY + 8, 60, 25);
  } else {
    // Draw signature line
    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.5);
    doc.line(pageWidth / 2 + 10, startY + 30, pageWidth - 25, startY + 30);
  }
  
  return startY + 40;
};

// Export functions for different document types
export const generateQuotePDF = (quote, user, signatureData = null) => {
  const doc = new jsPDF();
  
  drawHeader(doc, "PREVENTIVO", quote.quote_number, quote.created_at?.split("T")[0] || new Date().toISOString().split("T")[0], user);
  
  let y = drawCustomerVehicleSection(doc, {
    name: quote.customer_name,
    fiscal_code: quote.customer_fiscal_code,
    address: quote.customer_address,
    phone: quote.customer_phone,
    email: quote.customer_email
  }, {
    brand: quote.vehicle_brand,
    model: quote.vehicle_model,
    version: quote.vehicle_version,
    plate: quote.vehicle_plate,
    year: quote.vehicle_year,
    km: quote.vehicle_km,
    fuel_type: quote.vehicle_fuel_type,
    engine_code: quote.vehicle_engine_code,
    vin: quote.vehicle_vin
  }, 58);
  
  y = drawItemsTable(doc, quote.items, quote.labor_cost || 0, quote.labor_hours || 0, y + 5);
  
  // Additional costs section (waste disposal, consumables)
  const wasteDisposal = parseFloat(quote.waste_disposal_cost) || 0;
  const consumables = parseFloat(quote.consumables_cost) || 0;
  if (wasteDisposal > 0 || consumables > 0) {
    doc.setFontSize(8);
    doc.setTextColor(...colors.gray);
    doc.setFont("helvetica", "bold");
    doc.text("Costi aggiuntivi:", 15, y + 10);
    doc.setFont("helvetica", "normal");
    let costY = y + 16;
    if (wasteDisposal > 0) {
      doc.text(`Smaltimento rifiuti: € ${wasteDisposal.toFixed(2)}`, 15, costY);
      costY += 5;
    }
    if (consumables > 0) {
      doc.text(`Materiali di consumo: € ${consumables.toFixed(2)}`, 15, costY);
      costY += 5;
    }
    y += 15;
  }
  
  // Notes
  if (quote.notes) {
    doc.setFontSize(8);
    doc.setTextColor(...colors.gray);
    doc.setFont("helvetica", "bold");
    doc.text("Note:", 15, y + 10);
    doc.setFont("helvetica", "normal");
    const noteLines = doc.splitTextToSize(quote.notes, 80);
    doc.text(noteLines, 15, y + 16);
  }
  
  drawTotals(doc, quote.subtotal, quote.vat_total, quote.total, y + 8);
  
  // Validity
  doc.setFontSize(8);
  doc.setTextColor(...colors.gray);
  doc.text(`Validità preventivo: ${quote.validity_days || 30} giorni`, 15, y + 60);
  
  drawFooter(doc);
  
  return doc;
};

export const generateInvoicePDF = (invoice, user) => {
  const doc = new jsPDF();
  
  drawHeader(doc, "FATTURA", invoice.invoice_number, invoice.created_at?.split("T")[0] || new Date().toISOString().split("T")[0], user);
  
  let y = drawCustomerVehicleSection(doc, {
    name: invoice.customer_name,
    fiscal_code: invoice.customer_fiscal_code,
    address: invoice.customer_address,
    phone: invoice.customer_phone,
    email: invoice.customer_email
  }, {
    brand: invoice.vehicle_brand,
    model: invoice.vehicle_model,
    version: invoice.vehicle_version,
    plate: invoice.vehicle_plate,
    year: invoice.vehicle_year,
    km: invoice.vehicle_km,
    fuel_type: invoice.vehicle_fuel_type,
    engine_code: invoice.vehicle_engine_code,
    vin: invoice.vehicle_vin
  }, 58);
  
  y = drawItemsTable(doc, invoice.items, invoice.labor_cost || 0, invoice.labor_hours || 0, y + 5);
  
  drawTotals(doc, invoice.subtotal, invoice.vat_total, invoice.total, y + 8);
  
  // Payment info
  doc.setFontSize(8);
  doc.setTextColor(...colors.gray);
  doc.text(`Da Preventivo: #${invoice.quote_number}`, 15, y + 60);
  
  drawFooter(doc);
  
  return doc;
};

export const generateAcceptancePDF = async (acceptance, user, signatureData = null, damageImageData = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Use acceptance_number if available, otherwise fallback to vehicle_plate
  const docNumber = acceptance.acceptance_number || acceptance.vehicle_plate;
  drawHeader(doc, "ACCETTAZIONE", docNumber, acceptance.created_at?.split("T")[0] || new Date().toISOString().split("T")[0], user);
  
  let y = drawCustomerVehicleSection(doc, {
    name: acceptance.customer_name,
    fiscal_code: acceptance.customer_fiscal_code,
    address: acceptance.customer_address,
    city: acceptance.customer_city,
    province: acceptance.customer_province,
    cap: acceptance.customer_cap,
    phone: acceptance.customer_phone,
    email: acceptance.customer_email
  }, {
    brand: acceptance.vehicle_brand,
    model: acceptance.vehicle_model,
    version: acceptance.vehicle_version,
    plate: acceptance.vehicle_plate,
    year: acceptance.vehicle_year,
    km: acceptance.vehicle_km,
    fuel_type: acceptance.vehicle_fuel_type,
    engine_code: acceptance.vehicle_engine_code,
    vin: acceptance.vehicle_vin
  }, 55);
  
  // Check if there are damage marks - compact version
  const hasDamageMarks = acceptance.damage_data?.marks && acceptance.damage_data.marks.length > 0;
  
  if (hasDamageMarks && damageImageData) {
    // DAMAGE DIAGRAM - very compact
    const imgWidth = (pageWidth - 30) * 0.35; // 35% of width
    const imgHeight = 30; // Reduced height
    
    doc.setFillColor(...colors.white);
    doc.setDrawColor(...colors.border);
    doc.roundedRect(15, y, imgWidth + 10, imgHeight + 12, 2, 2, 'FD');
    
    doc.setFillColor(...colors.primary);
    doc.roundedRect(15, y, imgWidth + 10, 6, 2, 2, 'F');
    doc.setTextColor(...colors.white);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text("STATO CARROZZERIA", 18, y + 4.5);
    
    try {
      doc.addImage(damageImageData, 'PNG', 18, y + 9, imgWidth, imgHeight);
    } catch (e) {
      console.error("Error adding damage image:", e);
    }
    
    // Reported issues next to damage diagram
    const issuesX = 15 + imgWidth + 15;
    const issuesWidth = pageWidth - issuesX - 15;
    
    doc.setFillColor(...colors.white);
    doc.roundedRect(issuesX, y, issuesWidth, imgHeight + 12, 2, 2, 'FD');
    
    doc.setFillColor(...colors.primary);
    doc.roundedRect(issuesX, y, issuesWidth, 6, 2, 2, 'F');
    doc.setTextColor(...colors.white);
    doc.setFontSize(7);
    doc.text("PROBLEMI SEGNALATI", issuesX + 3, y + 4.5);
    
    doc.setTextColor(...colors.black);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    const issuesText = doc.splitTextToSize(acceptance.reported_issues || "Nessuno", issuesWidth - 8);
    doc.text(issuesText.slice(0, 6), issuesX + 3, y + 12);
    
    y += imgHeight + 16;
  } else {
    // Only reported issues (no damage diagram)
    doc.setFillColor(...colors.white);
    doc.roundedRect(15, y, pageWidth - 30, 22, 2, 2, 'FD');
    
    doc.setFillColor(...colors.primary);
    doc.roundedRect(15, y, pageWidth - 30, 6, 2, 2, 'F');
    doc.setTextColor(...colors.white);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text("PROBLEMI SEGNALATI", 18, y + 4.5);
    
    doc.setTextColor(...colors.black);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    const issuesText = doc.splitTextToSize(acceptance.reported_issues || "Nessuno", pageWidth - 40);
    doc.text(issuesText.slice(0, 3), 18, y + 12);
    
    y += 26;
  }
  
  // Accessories and fuel - compact single line
  doc.setFontSize(7);
  doc.setTextColor(...colors.gray);
  const accessoriesLine = [];
  if (acceptance.fuel_level) accessoriesLine.push(`Carburante: ${acceptance.fuel_level}`);
  if (acceptance.accessories?.length > 0) accessoriesLine.push(`Accessori: ${acceptance.accessories.slice(0, 5).join(", ")}`);
  doc.text(accessoriesLine.join(" | "), 15, y + 4);
  
  y += 8;
  
  // Test drive authorization - compact
  if (acceptance.authorize_test_drive) {
    doc.setFillColor(...colors.success);
  } else {
    doc.setFillColor(239, 68, 68);
  }
  doc.roundedRect(15, y, pageWidth - 30, 8, 2, 2, 'F');
  
  doc.setTextColor(...colors.white);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  const testDriveText = acceptance.authorize_test_drive 
    ? "✓ AUTORIZZA PROVA SU STRADA"
    : "✗ NON AUTORIZZA PROVA SU STRADA";
  doc.text(testDriveText, pageWidth / 2, y + 5.5, { align: 'center' });
  
  y += 12;
  
  // CONDITIONS OF INTERVENTION - Compact version
  const conditionsText = `L'intervento di manutenzione sul Suo veicolo sarà seguito in conformità al regolamento europeo 461/2010. L'officina garantisce l'utilizzo di pezzi di ricambio originali o di qualità equivalente. Firmando il seguente documento Lei dichiara di:
1) Autorizzare l'Officina ad eseguire i lavori specificati e PREVIA NOTIFICA ad eseguirne altri necessari, ai prezzi comunicati.
2) Effettuare il pagamento all'atto della consegna del veicolo, salvo diversi accordi, consapevole del diritto di ritenzione (art. 2756 C.C.).
3) Autorizzare prove su strada: in caso di incidenti, l'officina sarà responsabile della sola sostituzione delle parti danneggiate.
4) Rinunciare alle parti sostituite, se non richieste all'atto della consegna. Le parti in garanzia saranno trattenute dalla casa costruttrice.
5) Accettare il foro competente della sede di Officina per controversie.
6) Esonerare l'Officina da responsabilità per furto/smarrimento/deterioramento di beni lasciati nel veicolo.
7) Aver ricevuto informazioni sulla protezione dei dati personali. Le clausole 1-3-6 sono approvate ai sensi art. 1341 C.C.`;

  // Draw conditions box - compact
  const conditionsHeight = 55;
  doc.setFillColor(...colors.white);
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(15, y, pageWidth - 30, conditionsHeight, 2, 2, 'FD');
  
  doc.setFillColor(59, 130, 246); // Blue
  doc.roundedRect(15, y, pageWidth - 30, 6, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  doc.text("CONDIZIONI DI INTERVENTO - REGOLAMENTO UE 461/2010", 18, y + 4.5);
  
  doc.setTextColor(...colors.black);
  doc.setFontSize(5);
  doc.setFont("helvetica", "normal");
  const conditionsLines = doc.splitTextToSize(conditionsText, pageWidth - 36);
  doc.text(conditionsLines, 17, y + 10, { lineHeightFactor: 1.2 });
  
  y += conditionsHeight + 4;
  
  // Signature area - compact
  const signatureY = y;
  const signatureHeight = 30;
  
  doc.setFillColor(...colors.white);
  doc.setDrawColor(...colors.border);
  doc.roundedRect(15, signatureY, pageWidth - 30, signatureHeight, 2, 2, 'FD');
  
  // Split into two columns
  const signColWidth = (pageWidth - 30) / 2;
  
  // Left: Client signature
  doc.setFontSize(7);
  doc.setTextColor(...colors.gray);
  doc.text("Firma Cliente per accettazione:", 20, signatureY + 8);
  
  if (signatureData) {
    try {
      doc.addImage(signatureData, 'PNG', 20, signatureY + 10, 60, 16);
    } catch (e) {
      console.error("Error adding signature:", e);
    }
  }
  
  // Right: Workshop signature
  doc.text("Firma Officina:", 20 + signColWidth, signatureY + 8);
  doc.text("_______________________", 20 + signColWidth, signatureY + 24);
  
  // Date
  doc.setFontSize(6);
  doc.text(`Data: ${new Date().toLocaleDateString('it-IT')}`, pageWidth - 40, signatureY + signatureHeight - 4);
  
  drawFooter(doc);
  
  return doc;
};

export default {
  generateQuotePDF,
  generateInvoicePDF,
  generateAcceptancePDF
};
